import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import SpeechToText from '@/components/SpeechToText';
import { Calendar } from 'lucide-react';
import AppShell from '@/components/AppShell';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WeeklyReview = () => {
  const navigate = useNavigate();
  const [weekData, setWeekData] = useState(null);
  const [formData, setFormData] = useState({
    biggest_win: '',
    improvement_area: '',
    next_week_focus: ''
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchWeekData();
  }, []);

  const fetchWeekData = async () => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/weekly-reviews/current`, {
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });

      setWeekData(response.data);
      
      // Pre-fill if review already exists
      if (response.data.existing_review) {
        setFormData({
          biggest_win: response.data.existing_review.biggest_win || '',
          improvement_area: response.data.existing_review.improvement_area || '',
          next_week_focus: response.data.existing_review.next_week_focus || ''
        });
      }
    } catch (error) {
      console.error('Failed to fetch week data:', error);
      toast.error('Failed to load week data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.biggest_win.trim()) {
      toast.error('Please share your biggest win');
      return;
    }

    setSubmitting(true);
    try {
      const sessionToken = localStorage.getItem('session_token');
      await axios.post(
        `${API}/weekly-reviews`,
        formData,
        {
          headers: { 'Authorization': `Bearer ${sessionToken}` }
        }
      );

      toast.success('Weekly review saved!');
      setTimeout(() => navigate('/dashboard'), 1500);
    } catch (error) {
      console.error('Failed to save review:', error);
      toast.error('Failed to save review');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <AppShell>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-navy-900 border-r-transparent"></div>
            <p className="mt-4 text-gray-600">Loading...</p>
          </div>
        </div>
      </AppShell>
    );
  }

  const weekStart = weekData?.week_start ? new Date(weekData.week_start) : new Date();
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);

  return (
    <AppShell>
      <div className="max-w-4xl mx-auto p-4 md:p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Calendar className="w-8 h-8" style={{ color: 'var(--navy-900)' }} />
              <h1 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: 'Manrope', color: 'var(--navy-900)' }}>
                Weekly Review
              </h1>
            </div>
            <p className="text-gray-600">
              Week of {weekStart.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} - {weekEnd.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
          </div>
        </div>

        {/* Wins This Week */}
        {weekData?.wins_this_week && weekData.wins_this_week.length > 0 && (
          <div className="bg-white p-6 rounded-lg border border-gray-200 mb-6">
            <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Manrope', color: 'var(--navy-900)' }}>
              Your Wins This Week ({weekData.win_count})
            </h2>
            <div className="space-y-3">
              {weekData.wins_this_week.map((win) => (
                <div key={win.win_id} className="p-3 rounded" style={{ backgroundColor: 'var(--navy-50)' }}>
                  <div className="flex justify-between items-start mb-1">
                    {win.category && (
                      <span className="text-xs px-2 py-1 rounded" style={{ backgroundColor: 'var(--cream-100)', color: 'var(--navy-900)' }}>
                        {win.category}
                      </span>
                    )}
                    <span className="text-xs text-gray-500" style={{ fontFamily: 'JetBrains Mono' }}>
                      {new Date(win.win_date).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700">
                    {win.use_enhanced && win.enhanced_text ? win.enhanced_text : win.original_text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Review Form */}
        <div className="bg-white p-6 md:p-8 rounded-lg border border-gray-200">
          <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Manrope', color: 'var(--navy-900)' }}>
            Weekly Reflection
          </h2>

          <div className="space-y-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium" style={{ color: 'var(--navy-900)' }}>
                  What was your biggest win this week? <span className="text-red-500">*</span>
                </label>
                <SpeechToText 
                  onTranscript={(text) => setFormData({...formData, biggest_win: formData.biggest_win + (formData.biggest_win ? ' ' : '') + text})}
                  buttonStyle="icon"
                />
              </div>
              <Textarea
                data-testid="biggest-win-input"
                value={formData.biggest_win}
                onChange={(e) => setFormData({ ...formData, biggest_win: e.target.value })}
                placeholder="Describe your most significant accomplishment..."
                className="min-h-24"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium" style={{ color: 'var(--navy-900)' }}>
                  What could have gone better?
                </label>
                <SpeechToText 
                  onTranscript={(text) => setFormData({...formData, improvement_area: formData.improvement_area + (formData.improvement_area ? ' ' : '') + text})}
                  buttonStyle="icon"
                />
              </div>
              <Textarea
                data-testid="improvement-input"
                value={formData.improvement_area}
                onChange={(e) => setFormData({ ...formData, improvement_area: e.target.value })}
                placeholder="What challenges did you face? What would you do differently?"
                className="min-h-24"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium" style={{ color: 'var(--navy-900)' }}>
                  Focus for next week:
                </label>
                <SpeechToText 
                  onTranscript={(text) => setFormData({...formData, next_week_focus: formData.next_week_focus + (formData.next_week_focus ? ' ' : '') + text})}
                  buttonStyle="icon"
                />
              </div>
              <Textarea
                data-testid="focus-input"
                value={formData.next_week_focus}
                onChange={(e) => setFormData({ ...formData, next_week_focus: e.target.value })}
                placeholder="What are your priorities for the upcoming week?"
                className="min-h-24"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                data-testid="save-review-btn"
                onClick={handleSubmit}
                disabled={submitting}
                className="flex-1 py-3 rounded-lg font-medium transition-colors duration-200"
                style={{ backgroundColor: 'var(--navy-900)', color: 'white' }}
              >
                {submitting ? 'Saving...' : weekData?.existing_review ? 'Update Review' : 'Save Review'}
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/dashboard')}
                className="px-6"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>

        {/* Past Reviews */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            Regular reflection helps you stay on track with your career goals
          </p>
        </div>
      </div>
    </AppShell>
  );
};

export default WeeklyReview;